/**
 * Socket.IO Client Service
 * 
 * Centralized Socket.IO connection management with automatic reconnection,
 * authentication, and event handling. Provides a singleton service for
 * managing real-time connections across the application.
 * 
 * References:
 * - backend/realtime/socket.js - Backend Socket.IO implementation
 * - src/pages/UnifiedDashboard.jsx - Reference implementation pattern
 * - src/security/SecureApiClient.js - Token retrieval pattern
 */

import { io } from 'socket.io-client';
import logger from '../utils/logger';
import { API_BASE_URL, API_CONFIG } from '../config/api.config';

const isRealtimeUpdatesEnabled = () => API_CONFIG.enableRealTimeUpdates !== false;

/** PM workflow event names — used by usePMNotifications for toast + cache invalidation */
export const PM_EVENTS = [
  'pm.rfi.created', 'pm.rfi.submit', 'pm.rfi.answer', 'pm.rfi.review',
  'pm.rfi.close', 'pm.rfi.reopen', 'pm.rfi.void', 'pm.rfi.reject',
  'pm.rfi.response_added',
  'pm.submittal.created', 'pm.submittal.submit', 'pm.submittal.review',
  'pm.submittal.approve', 'pm.submittal.approve_as_noted', 'pm.submittal.reject',
  'pm.submittal.revise', 'pm.submittal.resubmit', 'pm.submittal.close',
  'pm.submittal.revision_added', 'pm.submittal.approval_recorded',
  'pm.permit.created', 'pm.permit.submit', 'pm.permit.approve', 'pm.permit.reject',
  'pm.permit.activate', 'pm.permit.complete', 'pm.permit.expire',
  'pm.permit.suspend', 'pm.permit.reactivate', 'pm.permit.revoke',
  'pm.permit.request_changes',
  'pm.punch-item.created', 'pm.punch-item.initiate', 'pm.punch-item.start_work',
  'pm.punch-item.submit_for_review', 'pm.punch-item.accept', 'pm.punch-item.reject',
  'pm.punch-item.rework', 'pm.punch-item.escalate', 'pm.punch-item.close',
  'pm.punch-item.reopen',
  'pm.change-order.created', 'pm.change-order.submit', 'pm.change-order.approve',
  'pm.change-order.reject', 'pm.change-order.execute', 'pm.change-order.revise',
  'pm.change-order.price', 'pm.change-order.cancel',
  'pm.meeting.created', 'pm.meeting.action_assigned', 'pm.meeting.action_updated',
  'pm.meeting.occurred',
  'pm.daily-log.completed', 'pm.daily-log.distributed',
  'pm.toolbox.delivered',
  'pm.pm-document.revision_added',
  'pm.project.activate', 'pm.project.hold', 'pm.project.complete',
  'pm.project.resume', 'pm.project.reopen',
];

class SocketService {
  constructor() {
    this.socket = null;
    this.connectionStatus = isRealtimeUpdatesEnabled() ? 'disconnected' : 'disabled'; // 'disabled' | 'disconnected' | 'connecting' | 'connected' | 'error'
    this.eventSubscriptions = new Map(); // eventName -> Set<callback>
    this.connectionStatusListeners = new Set(); // Set<callback>
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  /**
   * Get JWT token from localStorage
   * Supports both vh_auth_token and access_token for compatibility
   */
  getToken() {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('vh_auth_token') || localStorage.getItem('access_token');
  }

  /**
   * Get backend URL from environment or use default
   */
  getBackendUrl() {
    return API_BASE_URL;
  }

  isRealtimeEnabled() {
    return isRealtimeUpdatesEnabled();
  }

  /**
   * Connect to Socket.IO server
   */
  connect() {
    if (!this.isRealtimeEnabled()) {
      logger.debug('[SocketService] Socket.IO disabled for this environment');
      this.setConnectionStatus('disabled');
      return;
    }

    if (this.socket?.connected) {
      logger.info('[SocketService] Already connected');
      return;
    }

    if (this.socket) {
      const token = this.getToken();
      this.socket.auth = { token };
      logger.info('[SocketService] Reconnecting existing socket instance');
      this.setConnectionStatus('connecting');
      this.socket.connect();
      return;
    }

    const token = this.getToken();
    const backendUrl = this.getBackendUrl();

    if (!token) {
      logger.warn('[SocketService] No token found, connection may fail');
    }

    logger.info('[SocketService] Connecting to Socket.IO...', { backendUrl });

    this.setConnectionStatus('connecting');

    this.socket = io(backendUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });

    this.setupEventListeners();
  }

  /**
   * Setup Socket.IO event listeners
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Native Socket.IO transport connection event
    this.socket.on('connect', () => {
      logger.info('[SocketService] Socket.IO transport connected', { socketId: this.socket?.id });
      this.setConnectionStatus('connected');
      this.reconnectAttempts = 0;
    });

    // Connection established
    this.socket.on('connected', (data) => {
      logger.info('[SocketService] Socket.IO connected', data);
      this.setConnectionStatus('connected');
      this.reconnectAttempts = 0;
    });

    // Disconnect event
    this.socket.on('disconnect', (reason) => {
      logger.warn('[SocketService] Socket.IO disconnected', { reason });
      this.setConnectionStatus('disconnected');
    });

    // Reconnect event
    this.socket.on('reconnect', (attemptNumber) => {
      logger.info('[SocketService] Socket.IO reconnected', { attemptNumber });
      this.setConnectionStatus('connected');
      this.reconnectAttempts = 0;
    });

    // Reconnect attempt - refresh auth token
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      logger.info('[SocketService] Socket.IO reconnect attempt', { attemptNumber });
      this.reconnectAttempts = attemptNumber;
      this.setConnectionStatus('connecting');
      
      // Refresh auth token before reconnect
      const token = this.getToken();
      if (token && this.socket) {
        this.socket.auth = { token };
        logger.debug('[SocketService] Refreshed auth token for reconnect');
      }
    });

    // Reconnect failed
    this.socket.on('reconnect_failed', () => {
      logger.error('[SocketService] Socket.IO reconnect failed');
      this.setConnectionStatus('error');
    });

    // Error event
    this.socket.on('error', (error) => {
      logger.error('[SocketService] Socket.IO error', { error });
      this.setConnectionStatus('error');
    });

    // Connect error event
    this.socket.on('connect_error', (error) => {
      logger.warn('[SocketService] Socket.IO connect error', {
        message: error.message,
        type: error.type,
        description: error.description,
      });
      this.setConnectionStatus('error');

      // Local API often runs without Socket.IO mounted — stop retry spam in the console.
      const msg = `${error.message || ''}`.toLowerCase();
      if (msg.includes('404') || msg.includes('websocket error') || msg.includes('xhr poll error')) {
        if (this.socket?.io?.opts) {
          this.socket.io.opts.reconnection = false;
        }
        this.socket?.disconnect();
      }

      // If auth error, try refreshing token
      if (error.message?.includes('auth') || error.message?.includes('Authentication')) {
        logger.info('[SocketService] Authentication error detected, refreshing token...');
        this.refreshAuth();
      }
    });

    // Room join acknowledgment
    this.socket.on('joined', (data) => {
      logger.debug('[SocketService] Joined room', data);
    });

    // Room leave acknowledgment
    this.socket.on('left', (data) => {
      logger.debug('[SocketService] Left room', data);
    });

    // Forward backend events to subscribers
    this.socket.on('safety.submitted', (event) => {
      this.emitToSubscribers('safety.submitted', event);
    });

    this.socket.on('risk.updated', (event) => {
      this.emitToSubscribers('risk.updated', event);
    });

    this.socket.on('action.created', (event) => {
      this.emitToSubscribers('action.created', event);
    });

    this.socket.on('action.updated', (event) => {
      this.emitToSubscribers('action.updated', event);
    });

    this.socket.on('action.completed', (event) => {
      this.emitToSubscribers('action.completed', event);
    });

    this.socket.on('incident.reported', (event) => {
      this.emitToSubscribers('incident.reported', event);
    });

    // Task/plan live updates — emitted to the tenant room by
    // vh-api realtime/taskPlanLiveEvents.js; consumed by ForemanDashboard
    // (vh-web#267: without these relay entries, subscribers can never fire).
    this.socket.on('task.updated', (event) => {
      this.emitToSubscribers('task.updated', event);
    });

    this.socket.on('plan.updated', (event) => {
      this.emitToSubscribers('plan.updated', event);
    });

    // SafetyAgent real-time events
    this.socket.on('agent.action.created', (event) => {
      this.emitToSubscribers('agent.action.created', event);
    });

    // Permit real-time events
    this.socket.on('permit.approved', (event) => {
      this.emitToSubscribers('permit.approved', event);
    });

    this.socket.on('permit.rejected', (event) => {
      this.emitToSubscribers('permit.rejected', event);
    });

    this.socket.on('permit.status_changed', (event) => {
      this.emitToSubscribers('permit.status_changed', event);
    });

    this.socket.on('permit.submitted', (event) => {
      this.emitToSubscribers('permit.submitted', event);
    });

    this.socket.on('permit.escalated', (event) => {
      this.emitToSubscribers('permit.escalated', event);
    });

    this.socket.on('permit.expiring', (event) => {
      this.emitToSubscribers('permit.expiring', event);
    });

    // PM workflow real-time events (module-level PM_EVENTS)
    SocketService.PM_EVENTS = PM_EVENTS;

    PM_EVENTS.forEach((eventName) => {
      this.socket.on(eventName, (event) => {
        this.emitToSubscribers(eventName, event);
      });
    });

    // Hierarchy (worker) messaging — server emits via emitToUsers (see hierarchyMessageDeliveryService)
    this.socket.on('hierarchy.message', (event) => {
      this.emitToSubscribers('hierarchy.message', event);
    });
  }

  /**
   * Emit event to all subscribers
   */
  emitToSubscribers(eventName, data) {
    const subscribers = this.eventSubscriptions.get(eventName);
    if (subscribers) {
      subscribers.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          logger.error('[SocketService] Error in event subscriber', {
            eventName,
            error: error.message,
          });
        }
      });
    }
  }

  /**
   * Set connection status and notify listeners
   */
  setConnectionStatus(status) {
    if (this.connectionStatus === status) return;
    this.connectionStatus = status;
    this.connectionStatusListeners.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        logger.error('[SocketService] Error in connection status listener', {
          error: error.message,
        });
      }
    });
  }

  /**
   * Disconnect from Socket.IO server
   */
  disconnect() {
    if (this.socket) {
      logger.info('[SocketService] Disconnecting Socket.IO');
      this.socket.disconnect();
      this.socket = null;
      this.setConnectionStatus('disconnected');
      this.eventSubscriptions.clear();
    }
  }

  /**
   * Register event listener
   */
  on(eventName, callback) {
    if (!this.eventSubscriptions.has(eventName)) {
      this.eventSubscriptions.set(eventName, new Set());
    }
    this.eventSubscriptions.get(eventName).add(callback);
    logger.debug('[SocketService] Registered event listener', { eventName });
  }

  /**
   * Unregister event listener
   */
  off(eventName, callback) {
    const subscribers = this.eventSubscriptions.get(eventName);
    if (subscribers) {
      subscribers.delete(callback);
      if (subscribers.size === 0) {
        this.eventSubscriptions.delete(eventName);
      }
      logger.debug('[SocketService] Unregistered event listener', { eventName });
    }
  }

  /**
   * Emit event to server
   */
  emit(eventName, data) {
    if (this.socket?.connected) {
      this.socket.emit(eventName, data);
    } else {
      logger.warn('[SocketService] Cannot emit event, socket not connected', { eventName });
    }
  }

  /**
   * Join tenant room
   */
  joinTenantRoom(tenantId) {
    if (!tenantId) {
      logger.warn('[SocketService] Cannot join tenant room, tenantId required');
      return;
    }
    this.emit('join:tenant', { tenantId });
    logger.info('[SocketService] Joining tenant room', { tenantId });
  }

  /**
   * Join site room
   */
  joinSiteRoom(siteId) {
    if (!siteId) {
      logger.warn('[SocketService] Cannot join site room, siteId required');
      return;
    }
    this.emit('join:site', { siteId });
    logger.info('[SocketService] Joining site room', { siteId });
  }

  /**
   * Leave site room
   */
  leaveSiteRoom(siteId) {
    if (!siteId) {
      logger.warn('[SocketService] Cannot leave site room, siteId required');
      return;
    }
    this.emit('leave:site', { siteId });
    logger.info('[SocketService] Leaving site room', { siteId });
  }

  /**
   * Leave tenant room
   */
  leaveTenantRoom(tenantId) {
    if (!tenantId) {
      logger.warn('[SocketService] Cannot leave tenant room, tenantId required');
      return;
    }
    this.emit('leave:tenant', { tenantId });
    logger.info('[SocketService] Leaving tenant room', { tenantId });
  }

  /**
   * Generic room join (aliases to joinTenantRoom for backward compatibility)
   */
  joinRoom(roomId) {
    if (roomId?.startsWith('tenant:')) {
      this.joinTenantRoom(roomId.replace('tenant:', ''));
    } else if (roomId?.startsWith('site:')) {
      this.joinSiteRoom(roomId.replace('site:', ''));
    } else {
      this.emit('join', { room: roomId });
    }
  }

  /**
   * Generic room leave
   */
  leaveRoom(roomId) {
    if (roomId?.startsWith('tenant:')) {
      this.leaveTenantRoom(roomId.replace('tenant:', ''));
    } else if (roomId?.startsWith('site:')) {
      this.leaveSiteRoom(roomId.replace('site:', ''));
    } else {
      this.emit('leave', { room: roomId });
    }
  }

  /**
   * Get current connection status
   */
  getConnectionStatus() {
    return this.connectionStatus;
  }

  /**
   * Subscribe to connection status changes
   */
  onConnectionStatusChange(callback) {
    this.connectionStatusListeners.add(callback);
    // Immediately call with current status
    callback(this.connectionStatus);
    return () => {
      this.connectionStatusListeners.delete(callback);
    };
  }

  /**
   * Check if socket is connected
   */
  isConnected() {
    return this.socket?.connected === true && this.connectionStatus === 'connected';
  }

  /**
   * Refresh authentication token
   * Call this after token refresh/login to update socket auth
   */
  refreshAuth() {
    const token = this.getToken();
    if (this.socket && token) {
      this.socket.auth = { token };
      logger.info('[SocketService] Auth token refreshed');
      
      // If disconnected, try reconnecting
      if (!this.socket.connected) {
        logger.info('[SocketService] Attempting to reconnect with refreshed token...');
        this.socket.connect();
      }
    } else if (!token) {
      logger.warn('[SocketService] Cannot refresh auth: no token found');
    }
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
